import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import LanguageSwitcher from "src/components/LanguageSwitcher";
import { resetUserPassword } from "src/services/AuthenticationAPIs";

const Resetpassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  useLanguage(); // Initialize RTL support
  const [new_password, setNewPassword] = useState<string>("");

  // Validation states
  const [lengthCheck, setLengthCheck] = useState<boolean>(false);
  const [numCheck, setNumCheck] = useState<boolean>(false);
  const [capitalCheck, setCapitalCheck] = useState<boolean>(false);
  const [charCheck, setCharCheck] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const checkPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);

    // Validation checks
    setLengthCheck(value.length >= 8);
    setCharCheck(/[!@#$%^&*]/.test(value));
    setNumCheck(/[0-9]/.test(value));
    setCapitalCheck(/[A-Z]/.test(value));
  };

  const handleUpdatePassword = async (
    e: React.FormEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    if (!lengthCheck || !charCheck || !numCheck || !capitalCheck) {
      message.error(t("resetPassword.requirementsNotMet"));
      return;
    }

    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) {
      message.error(t("resetPassword.invalidToken"));
      return;
    }

    try {
      const data = await resetUserPassword({
        token,
        newPassword: new_password,
      });

      if (data.status) {
        message.success(data.message || t("resetPassword.updateSuccess"));
        setTimeout(() => navigate("/log-in"), 3000);
      } else {
        message.error(
          data.message || t("resetPassword.updateFailed")
        );
      }
    } catch {
      message.error(t("resetPassword.connectionError"));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  return (
    <>
      <div className="reset-pass-bg" style={{ backgroundImage: "url(/Icons/forget-password-bg.svg)" }}>
        <div className="logo-wrapper">
          <img src="/images/DpLogo.png" className="logo-design" alt="img" />
          <LanguageSwitcher />
        </div>
        <div className="app">
          <div className="signup-internal-container">
            <form className="formdesign">
              <img src="/Icons/open-lock-reset-pass.svg" className="open-lock" alt="userlock" />
              <h2 className="Reset-password">{t("resetPassword.resetPassword")}</h2>
              <p className="Reset-detail">
                {t("resetPassword.createNewPassword")}
              </p>
              <div className="password-field">
                <label>
                  {t("resetPassword.createPassword")}
                  <div className="password-input-container">
                    <input
                      value={new_password}
                      onChange={checkPassword}
                      type={showPassword ? "text" : "password"}
                    />
                    <div
                      className={"eye-icon"}
                      onClick={() => togglePasswordVisibility()}
                    >
                      {showPassword ? (
                        <img src="/Icons/open eye.svg" alt={t("resetPassword.hidePassword")} />
                      ) : (
                        <img src="/Icons/close eye.svg" alt={t("resetPassword.showPassword")} />
                      )}
                    </div>
                  </div>
                </label>
              </div>

              <div className="password-msgs">
                <div className="condition-row">
                  {lengthCheck ? (
                    <CheckCircleFilled className="tick-filled-icon" />
                  ) : (
                    <CloseCircleFilled className="cross-filled-icon" />
                  )}
                  <span
                    className={`condition-txt ${
                      lengthCheck ? "valid-msg" : "invalid-msg"
                    }`}
                  >
                    {t("resetPassword.requirementLength")}
                  </span>
                </div>
                <div className="condition-row">
                  {charCheck ? (
                    <CheckCircleFilled className="tick-filled-icon" />
                  ) : (
                    <CloseCircleFilled className="cross-filled-icon" />
                  )}
                  <span
                    className={`condition-txt ${
                      charCheck ? "valid-msg" : "invalid-msg"
                    }`}
                  >
                    {t("resetPassword.requirementSpecial")}
                  </span>
                </div>
                <div className="condition-row">
                  {numCheck ? (
                    <CheckCircleFilled className="tick-filled-icon" />
                  ) : (
                    <CloseCircleFilled className="cross-filled-icon" />
                  )}
                  <span
                    className={`condition-txt ${
                      numCheck ? "valid-msg" : "invalid-msg"
                    }`}
                  >
                    {t("resetPassword.requirementNumber")}
                  </span>
                </div>
                <div className="condition-row">
                  {capitalCheck ? (
                    <CheckCircleFilled className="tick-filled-icon" />
                  ) : (
                    <CloseCircleFilled className="cross-filled-icon" />
                  )}
                  <span
                    className={`condition-txt ${
                      capitalCheck ? "valid-msg" : "invalid-msg"
                    }`}
                  >
                    {t("resetPassword.requirementCapital")}
                  </span>
                </div>
              </div>

              <button
                style={{ marginTop: "2rem" }}
                onClick={e => handleUpdatePassword(e)}
                className="button"
              >
                {t("resetPassword.updatePassword")}
              </button>

              <p className="signup-page-text viewTerms_text">
                {t("resetPassword.rememberPassword")}&nbsp;
                <a href="/log-in">{t("resetPassword.signIn")}</a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Resetpassword;
